import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  ShieldAlert, BellRing, ListChecks, Filter, ArrowLeft,
  CheckCircle2, AlertTriangle, Info, RefreshCw, Eye, Mail,
  MessageSquarePlus,
} from "lucide-react";
import { toast } from "sonner";
import { InternalAdminLayout } from "./InternalAdmin";

// Slice C+ uses `InternalAdminLayout` directly (matches every other internal page).
const InternalShell = InternalAdminLayout;

const SEVERITY_STYLE = {
  info:    { Icon: Info,    cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  warning: { Icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 border-amber-200" },
  error:   { Icon: ShieldAlert, cls: "text-red-700 bg-red-50 border-red-200" },
};

function severityChip(sev) {
  const { Icon, cls } = SEVERITY_STYLE[sev] || SEVERITY_STYLE.info;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border ${cls}`}>
      <Icon className="w-3 h-3" /> {sev || "info"}
    </span>
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}

/* ------------------------------------------------------------------------- */
/* Tab 1: Tenant Timeline (audit_log)                                        */
/* ------------------------------------------------------------------------- */
function TenantTimeline() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ company_id: "", action: "", severity: "" });
  const [total, setTotal] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.action) params.action = filters.action;
      if (filters.severity) params.severity = filters.severity;
      const { data } = await api.get("/internal-admin/ops/audit-log", { params });
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load timeline");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5" data-testid="ops-timeline-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex items-center gap-2 mr-auto">
          <ListChecks className="w-5 h-5 text-emerald-800" />
          <h2 className="font-display text-xl font-semibold">Tenant timeline</h2>
          <Badge variant="secondary">{total.toLocaleString()}</Badge>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Company ID</label>
          <Input data-testid="ops-filter-company" value={filters.company_id} onChange={(e) => setFilters(f => ({...f, company_id: e.target.value}))} className="h-8 w-[260px] text-xs font-mono" placeholder="(all tenants)"/>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Action prefix</label>
          <Input data-testid="ops-filter-action" value={filters.action} onChange={(e) => setFilters(f => ({...f, action: e.target.value}))} className="h-8 w-[200px] text-xs font-mono" placeholder="e.g. provisioning."/>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Severity</label>
          <select data-testid="ops-filter-severity" value={filters.severity} onChange={(e) => setFilters(f => ({...f, severity: e.target.value}))} className="h-8 px-2 text-xs border rounded">
            <option value="">all</option>
            <option value="info">info</option>
            <option value="warning">warning</option>
            <option value="error">error</option>
          </select>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ops-timeline-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /></Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider font-medium text-muted-foreground border-b">
          <div className="col-span-2">When</div>
          <div className="col-span-1">Sev</div>
          <div className="col-span-3">Action</div>
          <div className="col-span-3">Company</div>
          <div className="col-span-3">Actor / details</div>
        </div>
        {loading && rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No audit entries match this filter.</div>
        ) : (
          rows.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-2.5 text-xs border-b last:border-b-0 hover:bg-slate-50/60 transition" data-testid="ops-timeline-row">
              <div className="col-span-2 font-mono text-[11px] text-muted-foreground">{formatWhen(r.created_at)}</div>
              <div className="col-span-1">{severityChip(r.severity)}</div>
              <div className="col-span-3 font-mono text-[11.5px]">{r.action}</div>
              <div className="col-span-3 truncate">
                {r.company_id ? <code className="text-[10.5px]">{r.company_id.slice(0,8)}…</code> : <span className="text-muted-foreground">—</span>}
                {(r.metadata?.tenant_name || r.new_value?.message) && <span className="ml-1 text-muted-foreground">{r.metadata?.tenant_name || r.new_value?.message}</span>}
              </div>
              <div className="col-span-3 truncate">
                {r.actor_email && <span className="text-[11px]">{r.actor_email}</span>}
                {(r.previous_value || r.new_value) && (
                  <details className="ml-1 inline">
                    <summary className="cursor-pointer text-[10px] text-emerald-700 hover:text-emerald-900">diff</summary>
                    <pre className="mt-1 max-w-md whitespace-pre-wrap break-all text-[10px] bg-slate-50 p-1.5 rounded border">{JSON.stringify({ prev: r.previous_value, next: r.new_value }, null, 2)}</pre>
                  </details>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 2: Red Flag Queue                                                     */
/* ------------------------------------------------------------------------- */
function RedFlagQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/internal-admin/ops/red-flags");
      setRows(data.rows || []);
    } catch (e) { toast.error("Failed to load red-flag queue"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const clearFlag = async (companyId) => {
    if (!window.confirm("Clear red-flag for this tenant? They will need to re-enter the provisioning pipeline manually.")) return;
    try {
      await api.post(`/internal-admin/ops/red-flags/${companyId}/clear`);
      toast.success("Red-flag cleared.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); }
  };

  return (
    <Card className="p-5" data-testid="ops-redflag-tab">
      <div className="flex items-center gap-2 mb-4">
        <ShieldAlert className="w-5 h-5 text-red-700" />
        <h2 className="font-display text-xl font-semibold">Red-flag queue</h2>
        <Badge variant="destructive">{rows.length}</Badge>
        <Button variant="outline" size="sm" className="ml-auto" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /></Button>
      </div>
      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-emerald-700 flex items-center justify-center gap-2"><CheckCircle2 className="w-4 h-4"/> No red-flagged tenants. Onboarding looking healthy.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="border-2 border-red-200 bg-red-50/60 rounded-lg p-4 flex items-start gap-3" data-testid="ops-redflag-row">
              <ShieldAlert className="w-5 h-5 text-red-700 shrink-0 mt-0.5"/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium tracking-tight">{r.name || "Unnamed"}</span>
                  <Badge variant="outline" className="text-[10px]">{r.provisioning_status || "?"}</Badge>
                </div>
                <div className="text-sm text-red-900 leading-snug mb-2">{r.red_flag_reason || "—"}</div>
                <div className="text-[11px] text-muted-foreground space-x-3">
                  <span>ID <code>{r.id?.slice(0,8)}…</code></span>
                  <span>Created {formatWhen(r.created_at)}</span>
                  {r.contact_email && <span>Email <a href={`mailto:${r.contact_email}`} className="underline">{r.contact_email}</a></span>}
                </div>
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                <Link to={`/internal-admin/tenants`} className="text-[11px] text-emerald-800 hover:underline inline-flex items-center gap-1"><Eye className="w-3 h-3"/> Open</Link>
                <Button size="sm" variant="outline" onClick={() => clearFlag(r.id)} data-testid={`ops-redflag-clear-${r.id?.slice(0,8)}`}>Clear flag</Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 3: Admin Notifications Inbox                                          */
/* ------------------------------------------------------------------------- */
function NotificationsInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unread, setUnread] = useState(0);
  const [onlyUnread, setOnlyUnread] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/internal-admin/ops/notifications", { params: { only_unread: onlyUnread } });
      setRows(data.rows || []);
      setUnread(data.unread || 0);
    } catch (e) { toast.error("Failed to load notifications"); }
    finally { setLoading(false); }
  }, [onlyUnread]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/internal-admin/ops/notifications/${id}/read`);
      load();
    } catch (e) { toast.error("Mark-read failed"); }
  };

  return (
    <Card className="p-5" data-testid="ops-inbox-tab">
      <div className="flex items-center gap-2 mb-4">
        <BellRing className="w-5 h-5 text-emerald-800" />
        <h2 className="font-display text-xl font-semibold">Admin notifications</h2>
        <Badge variant={unread > 0 ? "default" : "secondary"}>{unread} unread</Badge>
        <label className="ml-auto inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} data-testid="ops-inbox-unread-toggle"/>
          Only unread
        </label>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 ${loading?"animate-spin":""}`} /></Button>
      </div>
      {loading && rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground">No notifications.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((n) => (
            <div key={n.id} className={`border rounded-lg p-3.5 flex items-start gap-3 ${n.read ? "bg-slate-50/40 opacity-80" : "bg-white border-emerald-200"}`} data-testid="ops-inbox-row">
              <Mail className={`w-4 h-4 mt-0.5 shrink-0 ${n.read ? "text-slate-400" : "text-emerald-700"}`}/>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 text-sm">
                  {severityChip(n.severity)}
                  <code className="text-[11px] text-muted-foreground">{n.event_type}</code>
                  <span className="text-[11px] text-muted-foreground ml-auto">{formatWhen(n.created_at)}</span>
                </div>
                <div className="text-sm leading-snug">{n.summary}</div>
                {n.metadata && Object.keys(n.metadata).length > 0 && (
                  <pre className="mt-1.5 text-[10px] bg-slate-50 p-2 rounded border whitespace-pre-wrap break-all max-w-3xl">{JSON.stringify(n.metadata, null, 2)}</pre>
                )}
              </div>
              {!n.read && (
                <Button size="sm" variant="ghost" onClick={() => markRead(n.id)} data-testid={`ops-inbox-read-${n.id?.slice(0,8)}`}>Mark read</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 4: Provisioning status filter (lightweight tenant table)              */
/* ------------------------------------------------------------------------- */
const PROVISIONING_FILTERS = [
  { v: "",                label: "All tenants" },
  { v: "uploading",       label: "Uploading" },
  { v: "crawling",        label: "Crawling" },
  { v: "building_kb",     label: "Building KB" },
  { v: "generating_qa",   label: "Generating Q&A" },
  { v: "running_tests",   label: "Running tests" },
  { v: "finalizing",      label: "Finalizing" },
  { v: "ready",           label: "Ready" },
  { v: "blocked",         label: "Blocked" },
  { v: "red_flagged",     label: "Red-flagged" },
];

function StatusFilter() {
  const [status, setStatus] = useState("uploading");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status ? { provisioning_status: status } : {};
      const { data } = await api.get("/internal-admin/tenants", { params });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error("Failed to load tenants"); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5" data-testid="ops-statusfilter-tab">
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="w-5 h-5 text-emerald-800" />
        <h2 className="font-display text-xl font-semibold mr-3">Provisioning status</h2>
        {PROVISIONING_FILTERS.map((f) => (
          <button
            key={f.v}
            onClick={() => setStatus(f.v)}
            className={`text-xs px-3 py-1 rounded-full border transition ${status === f.v ? "bg-emerald-900 text-white border-emerald-900" : "bg-white text-foreground hover:border-emerald-700"}`}
            data-testid={`ops-status-filter-${f.v || "all"}`}
          >{f.label}</button>
        ))}
        <Badge variant="secondary" className="ml-auto">{rows.length}</Badge>
      </div>
      <div className="border rounded-lg overflow-hidden text-xs">
        <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 uppercase tracking-wider text-[10px] font-medium text-muted-foreground border-b">
          <div className="col-span-4">Tenant</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1">Flag</div>
          <div className="col-span-2">Resources</div>
          <div className="col-span-3">Updated</div>
        </div>
        {loading && rows.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">No tenants in this state.</div>
        ) : rows.map((r) => (
          <div key={r.id} className="grid grid-cols-12 gap-3 px-4 py-2 border-b last:border-b-0 hover:bg-slate-50/60" data-testid="ops-statusfilter-row">
            <div className="col-span-4">
              <div className="font-medium tracking-tight">{r.name}</div>
              <div className="text-[10px] text-muted-foreground font-mono">{r.id?.slice(0,8)}…</div>
            </div>
            <div className="col-span-2"><Badge variant="outline" className="text-[10px]">{r.provisioning_status || "?"}</Badge></div>
            <div className="col-span-1">{r.red_flag ? <ShieldAlert className="w-3.5 h-3.5 text-red-600"/> : <span className="text-muted-foreground">—</span>}</div>
            <div className="col-span-2">{r.resources ?? 0} files</div>
            <div className="col-span-3 text-muted-foreground text-[11px]">{formatWhen(r.provisioning_status_updated_at || r.updated_at)}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Tab 5: Assistant Interest Feedback                                        */
/* ------------------------------------------------------------------------- */
const FEEDBACK_TYPE_LABEL = {
  sales: "Sales AI Employee",
  product_specialist: "Product Specialist",
  training: "Training Assistant",
  launch: "Product Launch Assistant",
  support: "Customer Support Assistant",
  other: "Other",
};

function AssistantInterestFeedbackTab() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter) params.assistant_type = typeFilter;
      const { data } = await api.get(
        "/internal-admin/feedback/assistant-interest",
        { params },
      );
      setRows(data.rows || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Failed to load feedback");
    } finally { setLoading(false); }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5" data-testid="ops-feedback-tab">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2">
          <MessageSquarePlus className="w-4 h-4 text-emerald-700"/> Assistant interest feedback
        </h2>
        <Badge variant="outline" className="tabular-nums">{total} submissions</Badge>
        <div className="ml-auto flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Filter type:</span>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="h-8 px-2 text-xs border rounded"
            data-testid="ops-feedback-type-filter"
          >
            <option value="">All</option>
            {Object.entries(FEEDBACK_TYPE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="ops-feedback-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center" data-testid="ops-feedback-empty">
          No feedback submissions yet.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground bg-slate-50 border-b">
            <div className="col-span-3">Tenant</div>
            <div className="col-span-3">User</div>
            <div className="col-span-2">Requested</div>
            <div className="col-span-3">Note</div>
            <div className="col-span-1 text-right">When</div>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              className="grid grid-cols-12 gap-3 px-4 py-2.5 text-xs border-b last:border-b-0 hover:bg-slate-50/60 transition"
              data-testid="ops-feedback-row"
            >
              <div className="col-span-3 truncate font-medium" title={r.company_name}>
                {r.company_name || "—"}
              </div>
              <div className="col-span-3 truncate text-muted-foreground" title={r.user_email}>
                {r.user_email || "—"}
              </div>
              <div className="col-span-2">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                  {FEEDBACK_TYPE_LABEL[r.assistant_type] || r.assistant_type}
                </span>
              </div>
              <div className="col-span-3 text-muted-foreground italic truncate" title={r.note || ""}>
                {r.note ? `"${r.note}"` : "—"}
              </div>
              <div className="col-span-1 text-right tabular-nums text-[10px] text-muted-foreground">
                {formatWhen(r.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


/* ------------------------------------------------------------------------- */
/* Tab 6: Payment reconciliation (Gate C2E)                                  */
/*                                                                           */
/* The operator surface for post-payment work that stopped short of success: */
/* an ambiguous customer SMS, a Clover posting whose worker died mid-call, a */
/* payment whose finalization exhausted its retries, a shipping finalization */
/* that did not finish. Before this tab these rows were reachable only by a  */
/* direct MongoDB query, which is not an operations capability.              */
/*                                                                           */
/* Two rules this panel exists to enforce in the UI, not just the API:       */
/*   1. There is NO retry action, on any row. For an AMBIGUOUS outcome the   */
/*      provider may already have delivered, so a resend could charge a      */
/*      customer's attention twice or double-book a merchant's till.         */
/*   2. Closing an item is a judgement, so it requires a typed reason and an */
/*      explicit confirmation, and it is audited server-side.                */
/*                                                                           */
/* Authorization is the existing internal-admin gate: the route is mounted   */
/* under `InternalProtected` and every request is answered by the backend's  */
/* `get_internal_user` dependency (403 for any non-internal role).           */
/* ------------------------------------------------------------------------- */
const RECON_CLASSIFICATION = {
  ambiguous:         { label: "Ambiguous",        cls: "text-amber-800 bg-amber-50 border-amber-300" },
  exhausted_failure: { label: "Retries spent",    cls: "text-red-700 bg-red-50 border-red-200" },
  terminal_failure:  { label: "Terminal failure", cls: "text-red-700 bg-red-50 border-red-200" },
  retryable_failure: { label: "Failed",           cls: "text-orange-700 bg-orange-50 border-orange-200" },
  manually_resolved: { label: "Resolved",         cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  not_attempted:     { label: "Not attempted",    cls: "text-slate-700 bg-slate-50 border-slate-200" },
  in_progress:       { label: "In progress",      cls: "text-sky-700 bg-sky-50 border-sky-200" },
  succeeded:         { label: "Succeeded",        cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
};

const RECON_SOURCES = [
  { id: "effect", label: "Effect delivery" },
  { id: "finalization", label: "Payment finalization" },
  { id: "clover_accounting", label: "Clover accounting" },
  { id: "shipping_finalization", label: "Shipping finalization" },
];

function classificationChip(c) {
  const meta = RECON_CLASSIFICATION[c] || { label: c || "unknown", cls: "text-slate-700 bg-slate-50 border-slate-200" };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border ${meta.cls}`}
      data-testid={`recon-class-${c}`}
    >
      {c === "ambiguous" && <AlertTriangle className="w-3 h-3" />}
      {meta.label}
    </span>
  );
}

// Exported by name so the panel can be rendered directly in a test
// without standing up the whole internal-admin shell. The default
// export and the tab wiring are unchanged.
export function PaymentReconciliation() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    company_id: "", effect: "", source: "", state: "", min_age_hours: "",
  });
  const [skip, setSkip] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [resolving, setResolving] = useState(null);   // the item being closed
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: LIMIT, skip };
      if (filters.company_id) params.company_id = filters.company_id;
      if (filters.effect) params.effect = filters.effect;
      if (filters.source) params.source = filters.source;
      if (filters.state) params.state = filters.state;
      if (filters.min_age_hours) params.min_age_hours = filters.min_age_hours;
      const { data } = await api.get("/internal-admin/ops/reconciliation", { params });
      setRows(data.rows || []);
      setTruncated(Boolean(data.truncated));
    } catch (e) {
      // Surface the outage. An empty table would read as "nothing is
      // stranded", which is the opposite of what a 503 means.
      const detail = e?.response?.data?.detail;
      setError(
        detail === "reconciliation_store_unavailable"
          ? "The reconciliation store is unavailable — this list is NOT empty, it is unknown."
          : "Failed to load reconciliation items."
      );
      setRows([]);
    } finally { setLoading(false); }
  }, [filters, skip]);

  useEffect(() => { load(); }, [load]);

  const submitResolution = async () => {
    if (!resolving || reason.trim().length < 3) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/internal-admin/ops/reconciliation/${encodeURIComponent(resolving.item_id)}/resolve`,
        { reason: reason.trim() },
      );
      toast.success(
        data.status === "already_resolved"
          ? "Already resolved by someone else — nothing was repeated."
          : "Marked reviewed and resolved."
      );
      setResolving(null);
      setReason("");
      load();
    } catch (e) {
      const status = e?.response?.status;
      toast.error(
        status === 404 ? "That item no longer exists, or is not resolvable."
          : status === 503 ? "The reconciliation store is unavailable — nothing was changed."
          : "Could not resolve that item."
      );
    } finally { setSaving(false); }
  };

  return (
    <Card className="p-5" data-testid="ops-reconciliation-tab">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex items-center gap-2 mr-auto">
          <ShieldAlert className="w-5 h-5 text-emerald-800" />
          <h2 className="font-display text-xl font-semibold">Payment reconciliation</h2>
          <Badge variant="secondary" data-testid="recon-count">{rows.length}</Badge>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Company ID</label>
          <Input data-testid="recon-filter-company" value={filters.company_id}
                 onChange={(e) => { setSkip(0); setFilters(f => ({...f, company_id: e.target.value})); }}
                 className="h-8 w-[240px] text-xs font-mono" placeholder="(all tenants)"/>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Source</label>
          <select data-testid="recon-filter-source" value={filters.source}
                  onChange={(e) => { setSkip(0); setFilters(f => ({...f, source: e.target.value})); }}
                  className="h-8 px-2 text-xs border rounded">
            <option value="">all</option>
            {RECON_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Effect</label>
          <Input data-testid="recon-filter-effect" value={filters.effect}
                 onChange={(e) => { setSkip(0); setFilters(f => ({...f, effect: e.target.value})); }}
                 className="h-8 w-[200px] text-xs font-mono" placeholder="e.g. order_paid.customer_sms"/>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">State</label>
          <select data-testid="recon-filter-state" value={filters.state}
                  onChange={(e) => { setSkip(0); setFilters(f => ({...f, state: e.target.value})); }}
                  className="h-8 px-2 text-xs border rounded">
            <option value="">unresolved</option>
            <option value="resolved">resolved</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] text-muted-foreground block">Older than (h)</label>
          <Input data-testid="recon-filter-age" type="number" min="0" value={filters.min_age_hours}
                 onChange={(e) => { setSkip(0); setFilters(f => ({...f, min_age_hours: e.target.value})); }}
                 className="h-8 w-[100px] text-xs"/>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="recon-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      <p className="text-[11px] text-muted-foreground mb-3" data-testid="recon-no-retry-notice">
        There is deliberately no retry action here. An ambiguous outcome may already
        have reached the provider, so the only safe operator action is to review the
        item and record what you found.
      </p>

      {error ? (
        <div className="p-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded"
             data-testid="recon-error">{error}</div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-slate-50 text-[11px] uppercase tracking-wider font-medium text-muted-foreground border-b">
            <div className="col-span-2">Tenant</div>
            <div className="col-span-3">Effect</div>
            <div className="col-span-2">Scope</div>
            <div className="col-span-2">Classification</div>
            <div className="col-span-2">Last seen</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {loading && rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="recon-loading">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground" data-testid="recon-empty">
              No unresolved reconciliation items match this filter.
            </div>
          ) : rows.map((r) => (
            <div key={r.item_id}
                 className="grid grid-cols-12 gap-3 px-4 py-2.5 text-xs border-b last:border-b-0 hover:bg-slate-50/60 transition"
                 data-testid="recon-row">
              <div className="col-span-2 truncate"><code className="text-[10.5px]">{r.company_id}</code></div>
              <div className="col-span-3 font-mono text-[11px] truncate" title={r.effect}>{r.effect}</div>
              <div className="col-span-2 font-mono text-[10.5px] truncate text-muted-foreground" title={r.scope_key}>{r.scope_key}</div>
              <div className="col-span-2">{classificationChip(r.classification)}</div>
              <div className="col-span-2 text-[11px] text-muted-foreground">
                {formatWhen(r.updated_at || r.created_at)}
                {r.last_error && <div className="font-mono text-[10px] truncate" title={r.last_error}>{r.last_error}</div>}
              </div>
              <div className="col-span-1 text-right">
                {r.resolvable ? (
                  <Button variant="outline" size="sm" className="h-7 text-[11px]"
                          onClick={() => { setResolving(r); setReason(""); }}
                          data-testid="recon-resolve-open">
                    <Eye className="w-3 h-3 mr-1" /> Review
                  </Button>
                ) : (
                  <span className="text-[10px] text-muted-foreground">—</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 mt-3">
        <Button variant="outline" size="sm" disabled={skip === 0 || loading}
                onClick={() => setSkip(s => Math.max(0, s - LIMIT))}
                data-testid="recon-prev">Previous</Button>
        <Button variant="outline" size="sm" disabled={!truncated || loading}
                onClick={() => setSkip(s => s + LIMIT)}
                data-testid="recon-next">Next</Button>
        <span className="text-[11px] text-muted-foreground" data-testid="recon-page">
          Showing {rows.length} from offset {skip}
        </span>
      </div>

      {resolving && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
             data-testid="recon-confirm-modal">
          <Card className="p-5 w-[520px] max-w-[92vw]">
            <h3 className="font-display text-lg font-semibold mb-1">Mark reviewed and resolved</h3>
            <p className="text-xs text-muted-foreground mb-3">
              This records your judgement. It performs no external action and sends
              nothing — it will not re-attempt the effect.
            </p>
            {resolving.classification === "ambiguous" && (
              <div className="text-[11px] text-amber-900 bg-amber-50 border border-amber-300 rounded p-2 mb-3"
                   data-testid="recon-ambiguous-warning">
                This outcome is <strong>ambiguous</strong>: the provider may already have
                delivered it. Confirm against the provider's own records before closing.
              </div>
            )}
            <div className="text-[11px] font-mono bg-slate-50 border rounded p-2 mb-3 break-all">
              {resolving.effect} · {resolving.company_id} · {resolving.scope_key}
            </div>
            <label className="text-[11px] text-muted-foreground block mb-1">
              Reason (required, recorded in the audit log)
            </label>
            <Input data-testid="recon-reason" value={reason} autoFocus
                   onChange={(e) => setReason(e.target.value)}
                   placeholder="e.g. confirmed delivered in the Twilio console"/>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" size="sm" onClick={() => { setResolving(null); setReason(""); }}
                      data-testid="recon-cancel">Cancel</Button>
              <Button size="sm" disabled={reason.trim().length < 3 || saving}
                      onClick={submitResolution} data-testid="recon-confirm">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                {saving ? "Saving…" : "Confirm resolution"}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* Page shell                                                                */
/* ------------------------------------------------------------------------- */
const TABS = [
  { id: "timeline", label: "Timeline",      Comp: TenantTimeline },
  { id: "redflag",  label: "Red flags",     Comp: RedFlagQueue },
  { id: "inbox",    label: "Notifications", Comp: NotificationsInbox },
  { id: "status",   label: "By status",     Comp: StatusFilter },
  { id: "feedback", label: "Feedback",      Comp: AssistantInterestFeedbackTab },
  { id: "recon",    label: "Reconciliation", Comp: PaymentReconciliation },
];

export default function InternalOperationsConsole() {
  const [tab, setTab] = useState("timeline");
  const Active = (TABS.find(t => t.id === tab) || TABS[0]).Comp;
  return (
    <InternalShell>
      <div className="mb-6">
        <Link to="/internal-admin" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 mb-3"><ArrowLeft className="w-3 h-3"/> Back to overview</Link>
        <h1 className="font-display text-3xl font-semibold tracking-tight">AI Operations Console</h1>
        <p className="text-muted-foreground text-sm mt-1">Read-only operational visibility into provisioning, approvals, red flags, and the audit trail — plus the payment-reconciliation queue, whose only write is an audited &ldquo;mark reviewed&rdquo;.</p>
      </div>
      <div className="flex gap-1 mb-5 border-b">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${tab === t.id ? "border-emerald-700 text-emerald-900" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            data-testid={`ops-tab-${t.id}`}
          >{t.label}</button>
        ))}
      </div>
      <Active />
    </InternalShell>
  );
}
